import { Button, Divider, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material'
import { type $Countersink } from 'metalworks-domain/src/__generated/admin-enhanced-model.js'
import { createAdminRecipe } from 'metalworks-domain/src/__generated/admin-implementation.js'
import { Countersink } from 'metalworks-domain/src/__generated/admin-model/index.js'
import { useCallback } from 'react'

import {
  DeleteEntityMenuItem,
  EditEntityMenuItem,
  EntityRow,
  ShowEntityMenuItem,
} from '@salsita-ai/platform-project-sdk/admin'
import { AttributeSwitchSelector, GenericDimensionInput } from '@salsita-ai/platform-project-sdk/react'
import { PhxIcon, PropertyContainer } from '@salsita-ai/platform-project-sdk/ui'

import { adminDomainHooks } from '../systems/admin-hooks.js'

const recipes = [
  createAdminRecipe({
    type: 'Countersink',
    bottomRadius: 5,
    topRadius: 7,
    plateThicknessMin: 3,
    plateThicknessMax: 10,
    visible: true,
  }),
]

export const CountersinksPageActions = () => {
  const { rootEntityId } = adminDomainHooks.useDomain()

  const { disabled, create } = adminDomainHooks.useCookedRecipes(recipes, rootEntityId)

  return (
    <Button
      color="primary"
      variant="contained"
      startIcon={<PhxIcon name="PlusBold" />}
      onClick={create}
      disabled={disabled}
    >
      Add countersink
    </Button>
  )
}

interface CountersinkSidebarProps {
  id: $Countersink['id']
}

export const CountersinkSidebar = (props: CountersinkSidebarProps) => {
  const { id } = props

  return (
    <PropertyContainer.Group spacing="medium">
      <PropertyContainer.Item title="Top diameter">
        <GenericDimensionInput entityId={id} instanceName="topRadius" />
      </PropertyContainer.Item>
      <PropertyContainer.Item title="Bottom diameter">
        <GenericDimensionInput entityId={id} instanceName="bottomRadius" />
      </PropertyContainer.Item>
      <PropertyContainer.Item title="Plate thickness (min)">
        <GenericDimensionInput entityId={id} instanceName="plateThicknessMin" />
      </PropertyContainer.Item>
      <PropertyContainer.Item title="Plate thickness (max)">
        <GenericDimensionInput entityId={id} instanceName="plateThicknessMax" />
      </PropertyContainer.Item>
      <PropertyContainer.Item title="Visible">
        <AttributeSwitchSelector entityId={id} instanceName="visible" />
      </PropertyContainer.Item>
    </PropertyContainer.Group>
  )
}

interface CountersinkActionsProps {
  id: $Countersink['id']
  onClose: () => void
}

const CountersinkActions = (props: CountersinkActionsProps) => {
  const { id, onClose } = props

  return (
    <>
      <EditEntityMenuItem id={id} onClose={onClose} title="Edit Countersink" bold />
      <Divider />
      <ShowEntityMenuItem id={id} onClose={onClose} />
      <DeleteEntityMenuItem id={id} onClose={onClose} />
    </>
  )
}

interface CountersinkRowProps {
  id: $Countersink['id']
}

// TODO: this should be an expression
const getCountersinkName = (countersink: Countersink) =>
  countersink.plateThicknessMin === countersink.plateThicknessMax
    ? `${countersink.plateThicknessMin} mm plate: ${countersink.topRadius * 2} x ${countersink.bottomRadius * 2} mm`
    : `${countersink.plateThicknessMin} - ${countersink.plateThicknessMax} mm plate: ${countersink.topRadius * 2} x ${countersink.bottomRadius * 2} mm`

const CountersinkRow = (props: CountersinkRowProps) => {
  const { id } = props

  const state = adminDomainHooks.useEntityState(id)
  const select = adminDomainHooks.useSelectEntity()

  const enter = useCallback(() => {
    select(id)
  }, [select, id])

  return (
    <EntityRow.Root
      key={state.id}
      id={state.id}
      visible={state.visible}
      onClick={enter}
      name={getCountersinkName(state)}
      showThumbnail={false}
      Actions={CountersinkActions}
    />
  )
}

export const CountersinksPage = () => {
  const materials = adminDomainHooks.useStoreSelector(e => {
    return e.state.entities.filter(Countersink.is)
  }, [])

  return (
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Name</TableCell>
            <TableCell sx={{ width: '20ch' }}>Status</TableCell>
            <TableCell sx={{ width: 62 }} />
          </TableRow>
        </TableHead>
        <TableBody>
          {materials.map(entity => (
            <CountersinkRow key={entity.id} id={entity.id} />
          ))}

          {materials.length === 0 && <EntityRow.EmptyRow title="No countersinks found" span={5} />}
        </TableBody>
      </Table>
    </TableContainer>
  )
}
